/**
 * Repository: the single API the UI uses for data.
 *
 * Every change is written to the device first (instant UI, works offline),
 * then queued in an outbox and pushed to Supabase in order. Each record carries
 * a client-generated id so a retry after a dropped connection can't create
 * duplicates. The server stays the source of truth for anything about access
 * or money (entitlement), which is never decided on the device.
 */
import type { CountSession, Entitlement, Kick, Locale, Plan, Profile } from '../domain/types';
import { DAY_MS } from '../domain/pregnancy';
import { config } from './config';
import { localStore, newId, type PendingOp } from './localStore';
import type { JournalEntry } from '../domain/journal';
import type { Moment, MomentKind } from '../domain/moments';

type Listener = () => void;
export interface AuthUser { id: string; email: string | null }

const listeners = new Set<Listener>();
let user: AuthUser | null = null;
let entitlement: Entitlement | null = null;
let syncing = false;
let retryTimer: number | undefined;
let backoffMs = 2000;
let lastError: string | null = null;

const emit = () => listeners.forEach(l => l());
const cloudMod = () => import('./cloud');

// ---------------------------------------------------------------- demo mode
function demoEntitlement(): Entitlement {
  let started = localStore.get().ui.demoTrialStartedAt;
  if (!started) { started = Date.now(); localStore.setUi({ demoTrialStartedAt: started }); }
  const trialEndsAt = started + 7 * DAY_MS;
  return { status: Date.now() < trialEndsAt ? 'trial' : 'free', trialEndsAt, paidUntil: null, plan: null, clockSkewMs: 0 };
}

// ---------------------------------------------------------------- sync engine
async function apply(op: PendingOp, userId: string): Promise<void> {
  const { cloud } = await cloudMod();
  switch (op.op) {
    case 'profile.save': return cloud.saveProfile(userId, op.profile);
    case 'consent.add': return cloud.addConsents(op.kinds, op.version);
    case 'kick.add': return cloud.addKick(op.kick);
    case 'kick.remove': return cloud.removeKick(op.clientId);
    case 'session.upsert': return cloud.upsertSession(op.session);
    case 'journal.add': return cloud.addJournal(op.entry);
    case 'journal.remove': return cloud.removeJournal(op.clientId);
    case 'moment.save': return cloud.saveMoment(op.moment);
    case 'moment.remove': return cloud.removeMoment(op.kind);
  }
}

function scheduleRetry() {
  window.clearTimeout(retryTimer);
  retryTimer = window.setTimeout(() => void flush(), backoffMs);
  backoffMs = Math.min(backoffMs * 2, 5 * 60_000);
}

async function flush(): Promise<void> {
  if (!config.cloud || !user || syncing) return;
  syncing = true;
  try {
    const { isTransient } = await cloudMod();
    for (let op = localStore.peek(); op; op = localStore.peek()) {
      try {
        await apply(op, user.id);
        localStore.dequeue();
        backoffMs = 2000;
        lastError = null;
      } catch (err) {
        if (isTransient(err)) { lastError = 'offline'; scheduleRetry(); break; }
        // Permanent rejection (e.g. failed a DB check). Drop it so the queue isn't stuck forever; report it.
        console.error('[sync] dropped operation', op.op, err);
        lastError = 'rejected';
        localStore.dequeue();
      }
    }
  } finally {
    syncing = false;
    emit();
  }
}

async function pull(): Promise<void> {
  if (!config.cloud || !user) return;
  const { cloud } = await cloudMod();
  try {
    const hasPendingProfile = localStore.get().outbox.some(o => o.op === 'profile.save');
    const [profile, data, ent] = await Promise.all([
      cloud.getProfile(),
      cloud.loadData(Date.now() - 300 * DAY_MS),
      cloud.getEntitlement()
    ]);
    if (profile && !hasPendingProfile) localStore.setProfile(profile);
    localStore.replaceData(data.kicks, data.sessions);
    entitlement = ent;
    // Journal is a newer table; if it isn't on the server yet, keep the local copy and carry on.
    try { localStore.replaceJournal(await cloud.loadJournal(Date.now() - 300 * DAY_MS)); }
    catch (e) { console.warn('[sync] journal pull failed', e); }
    try { localStore.replaceMoments(await cloud.loadMoments()); }
    catch (e) { console.warn('[sync] moments pull failed', e); }
  } catch (err) {
    console.warn('[sync] pull failed, using cached data', err);
  }
  emit();
}

function enqueue(op: PendingOp) {
  localStore.enqueue(op);
  emit();
  void flush();
}

// ---------------------------------------------------------------- public API
export const repo = {
  mode: config.cloud ? ('cloud' as const) : ('demo' as const),

  subscribe(l: Listener): () => void { listeners.add(l); return () => listeners.delete(l); },

  async init(): Promise<void> {
    if (config.cloud) {
      const { auth } = await cloudMod();
      try {
        const u = await auth.currentUser();
        await setUser(u ? { id: u.id, email: u.email ?? null } : null);
      } catch (e) { console.warn('[auth] offline at start, using cache', e); }
      // Trying it out before signing in: everything stays on this device until she makes an account.
      if (!user) entitlement = demoEntitlement();
      await auth.onChange(u => void setUser(u ? { id: u.id, email: u.email ?? null } : null));
      window.addEventListener('online', () => { backoffMs = 2000; void flush(); });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') { void flush().then(pull); }
      });
    } else {
      entitlement = demoEntitlement();
    }
    emit();
  },

  // ---- reads (synchronous, from the device cache)
  get user() { return user; },
  /** Using the app without an account yet: data lives only on this device until she signs in. */
  get guest(): boolean { return config.cloud && !user; },
  get locale(): Locale | null { return localStore.get().profile?.locale ?? localStore.get().locale; },
  get profile(): Profile | null { return localStore.get().profile; },
  get kicks(): Kick[] { return localStore.get().kicks; },
  get sessions(): CountSession[] { return localStore.get().sessions; },
  get journal(): JournalEntry[] { return localStore.get().journal; },
  get moments(): Moment[] { return localStore.get().moments; },
  get ui() { return localStore.get().ui; },
  get pending(): number { return localStore.get().outbox.length; },
  get syncProblem(): string | null { return lastError; },
  get entitlement(): Entitlement | null {
    return config.cloud ? entitlement : demoEntitlement();
  },
  /** Device time corrected to server time. Use for anything about access. */
  now(): number { return Date.now() + (entitlement?.clockSkewMs ?? 0); },

  // ---- writes
  setLocale(l: Locale) {
    localStore.setLocale(l);
    const p = localStore.get().profile;
    if (p) repo.saveProfile({ ...p, locale: l }); else emit();
  },
  setUi(patch: Parameters<typeof localStore.setUi>[0]) { localStore.setUi(patch); emit(); },

  saveProfile(p: Profile) {
    if (!localStore.get().ui.demoTrialStartedAt) localStore.setUi({ demoTrialStartedAt: Date.now() });
    localStore.setProfile(p);
    enqueue({ op: 'profile.save', profile: p });
  },

  recordConsents(kinds: string[]) {
    enqueue({ op: 'consent.add', kinds, version: config.consentVersion });
  },

  addKick(partial: Omit<Kick, 'clientId'>): Kick {
    const k: Kick = { clientId: newId(), ...partial };
    localStore.addKick(k);
    enqueue({ op: 'kick.add', kick: k });
    return k;
  },

  removeKick(clientId: string) {
    localStore.removeKick(clientId);
    enqueue({ op: 'kick.remove', clientId });
  },

  saveMoment(m: Moment) {
    localStore.saveMoment(m);
    enqueue({ op: 'moment.save', moment: m });
  },

  removeMoment(kind: MomentKind) {
    localStore.removeMoment(kind);
    enqueue({ op: 'moment.remove', kind });
  },

  addJournal(e: JournalEntry) {
    localStore.addJournal(e);
    enqueue({ op: 'journal.add', entry: e });
  },

  removeJournal(clientId: string) {
    localStore.removeJournal(clientId);
    enqueue({ op: 'journal.remove', clientId });
  },

  saveSession(s: CountSession) {
    localStore.upsertSession(s);
    enqueue({ op: 'session.upsert', session: s });
  },

  async refreshEntitlement(): Promise<void> {
    if (!config.cloud || !user) return;
    const { cloud } = await cloudMod();
    try { entitlement = await cloud.getEntitlement(); emit(); } catch { /* keep cached */ }
  },

  // ---- auth
  async sendEmailCode(email: string) { const { auth } = await cloudMod(); await auth.sendEmailCode(email); },
  async verifyEmailCode(email: string, code: string) {
    const { auth } = await cloudMod();
    const u = await auth.verifyEmailCode(email, code);
    await setUser({ id: u.id, email: u.email ?? null });
  },
  async signInWithGoogle() { const { auth } = await cloudMod(); await auth.signInWithGoogle(); },
  async signOut() {
    if (config.cloud) { const { auth } = await cloudMod(); await auth.signOut(); }
    const keepLocale = repo.locale;   // signing out shouldn't make her pick a language again
    localStore.wipe();
    if (keepLocale) localStore.setLocale(keepLocale);
    user = null; entitlement = config.cloud ? demoEntitlement() : null;
    emit();
  },

  // ---- payments
  async startCheckout(plan: Plan): Promise<string> {
    const { cloud } = await cloudMod();
    return cloud.startCheckout(plan);
  },

  // ---- PDPA
  async exportData(): Promise<unknown> {
    if (config.cloud && user) { await flush(); const { cloud } = await cloudMod(); return cloud.exportAll(); }
    const s = localStore.get();
    return { exported_at: new Date().toISOString(), profile: s.profile, kicks: s.kicks, sessions: s.sessions, journal: s.journal, moments: s.moments };
  },
  async deleteAccount(): Promise<void> {
    if (config.cloud && user) { const { cloud } = await cloudMod(); await cloud.deleteAccount(); }
    localStore.wipe();
    user = null; entitlement = null;
    emit();
  },

  /** Testing aid for demo mode only. Never available when connected to the real backend. */
  demo: config.cloud ? null : {
    seed(kicks: Kick[], sessions: CountSession[]) {
      kicks.forEach(k => localStore.addKick(k));
      sessions.forEach(s => localStore.upsertSession(s));
      emit();
    },
    expireTrial() { localStore.setUi({ demoTrialStartedAt: Date.now() - 8 * DAY_MS, trialPaywallSeen: false }); emit(); }
  }
};

async function setUser(u: AuthUser | null) {
  const changed = u?.id !== user?.id;
  const wasGuest = config.cloud && !localStore.get().ownerId;
  user = u;
  if (u) localStore.bindOwner(u.id);
  if (changed && u) {
    // She tried the app first, then signed in. Her kicks, notes and moments move into the account,
    // but if that account already has a profile, the one typed while trying must not overwrite it.
    if (wasGuest && localStore.get().outbox.some(o => o.op === 'profile.save')) {
      try {
        const { cloud } = await cloudMod();
        if (await cloud.getProfile()) localStore.dropOps('profile.save');
      } catch (e) { console.warn('[sync] could not check existing profile', e); }
    }
    await flush(); await pull();
  }
  emit();
}
